import { useEffect, useState } from "react";
import { Icon } from "./Icons";

export interface GlassNotice {
  id: string;
  title: string;
  body: string;
  persistent?: boolean;
  onOpen?: () => void;
  onComplete?: () => void;
  onSnooze?: () => void;
  actionLabel?: string;
  onAction?: () => void;
}

export function GlassNotificationHost({
  notices,
  dismiss,
}: {
  notices: GlassNotice[];
  dismiss: (id: string) => void;
}) {
  return (
    <div className="glass-notification-stack" aria-live="polite">
      {notices.slice(-3).map((notice) => (
        <GlassNotification key={notice.id} notice={notice} dismiss={dismiss} />
      ))}
    </div>
  );
}

function GlassNotification({
  notice,
  dismiss,
}: {
  notice: GlassNotice;
  dismiss: (id: string) => void;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    let timeout = 0;
    if (!notice.persistent)
      timeout = window.setTimeout(() => {
        setVisible(false);
        window.setTimeout(() => dismiss(notice.id), 250);
      }, 6500);
    return () => {
      cancelAnimationFrame(frame);
      if (timeout) window.clearTimeout(timeout);
    };
  }, [dismiss, notice.id, notice.persistent]);
  const close = () => {
    setVisible(false);
    window.setTimeout(() => dismiss(notice.id), 220);
  };
  return (
    <article className={`glass-notification ${visible ? "show" : ""}`}>
      <div className="notification-icon">
        <Icon name="bell" />
      </div>
      <div className="notification-copy">
        <strong>{notice.title}</strong>
        <span>{notice.body}</span>
        <div className="notification-actions">
          {notice.onOpen && (
            <button
              onClick={() => {
                notice.onOpen?.();
                close();
              }}
            >
              Open
            </button>
          )}
          {notice.onSnooze && (
            <button
              onClick={() => {
                notice.onSnooze?.();
                close();
              }}
            >
              Snooze
            </button>
          )}
          {notice.onComplete && (
            <button
              className="primary"
              onClick={() => {
                notice.onComplete?.();
                close();
              }}
            >
              Complete
            </button>
          )}
          {notice.onAction && notice.actionLabel && (
            <button
              className="primary"
              onClick={() => {
                notice.onAction?.();
                close();
              }}
            >
              {notice.actionLabel}
            </button>
          )}
        </div>
      </div>
      <button
        className="notification-close"
        onClick={close}
        aria-label="Dismiss"
      >
        <Icon name="close" />
      </button>
    </article>
  );
}
